import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { errorMessage } from "../../shared/api/errors.ts";
import { isJustClaimed, useDevices } from "../../shared/devices/devices.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { ArtEva } from "../../shared/ui/ArtEva/ArtEva.tsx";
import { Button, ButtonLink } from "../../shared/ui/Button/Button.tsx";
import { Feed } from "./Feed.tsx";
import styles from "./HomePage.module.css";

/** Главная и шторки поверх неё (вложенные маршруты, например сканер QR `/claim/scan`). */
export function HomePage() {
  return (
    <>
      <Home />
      <Outlet />
    </>
  );
}

/**
 * 07.01 — E-va нет, 07.02 — сразу после привязки, 07.03 — обычный вход.
 */
function Home() {
  const devices = useDevices();
  const justClaimed = useJustClaimed();

  if (devices.isError) return <LoadError error={devices.error} retry={() => void devices.refetch()} />;
  if (!devices.data) return <main className={styles.page} aria-busy="true" />;
  if (devices.data.length === 0) return <NoDevices />;
  return (
    <main className={styles.page}>
      <section className={styles.welcome}>
        {justClaimed ? (
          <Intro
            title="Устройство успешно подключено!"
            text="Теперь вы можете отправлять сообщения, менять оформление устройства и приглашать близких."
          />
        ) : (
          <Greeting />
        )}
        <div className={styles.actions}>
          <ButtonLink to="/compose" className={styles.compose}>
            Отправить сообщение
          </ButtonLink>
        </div>
      </section>
      <Feed devices={devices.data} />
    </main>
  );
}

/**
 * Флаг привязки из записи истории живёт до следующего перехода: главная запоминает его при показе
 * и тут же стирает из записи, чтобы «назад» и перезагрузка не показали 07.02 снова.
 */
function useJustClaimed(): boolean {
  const location = useLocation();
  const navigate = useNavigate();
  const [justClaimed] = useState(() => isJustClaimed(location.state));

  useEffect(() => {
    if (isJustClaimed(location.state)) {
      void navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
    }
  }, [location, navigate]);

  return justClaimed;
}

function LoadError({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <main className={`${styles.page} ${styles.centered}`}>
      <section className={styles.empty}>
        <p className={styles.description} role="alert">
          {errorMessage(error)}
        </p>
        <Button onClick={retry} className={styles.action}>
          Повторить
        </Button>
      </section>
    </main>
  );
}

/** 07.01 / MAIN / Empty — Registration Success (Figma 209:1771). */
function NoDevices() {
  return (
    <main className={`${styles.page} ${styles.centered}`}>
      <section className={styles.empty}>
        <ArtEva width={198.75} />
        <div className={styles.emptyText}>
          <h1 className={styles.title}>Добро пожаловать!</h1>
          <p className={styles.description}>
            Пока у вас нет подключенных устройств.
            <br />
            Добавьте свое первое устройство.
          </p>
        </div>
        <ButtonLink to="/claim/scan" className={styles.action}>
          Подключить E-va
        </ButtonLink>
      </section>
    </main>
  );
}

/** 07.03 / MAIN / Filled — Repeated Login Success (Figma 254:3637). */
function Greeting() {
  const { user } = useSession();
  const name = user?.displayName?.trim();
  // Время суток фиксируется при показе главной, а не пересчитывается на каждый рендер.
  const [greeting] = useState(() => greetingFor(new Date()));
  return <Intro title={name ? `${greeting}, ${name}!` : `${greeting}!`} text="Самое время отправить сообщение!" />;
}

/** Текстовый блок 07.02 (Figma 1029:23314) и 07.03 (Figma 254:3648). */
function Intro({ title, text }: { title: string; text: string }) {
  return (
    <div className={styles.intro}>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.description}>{text}</p>
    </div>
  );
}

function greetingFor(now: Date): string {
  const hour = now.getHours();
  if (hour >= 5 && hour < 12) return "Доброе утро";
  if (hour >= 12 && hour < 18) return "Добрый день";
  if (hour >= 18 && hour < 23) return "Добрый вечер";
  return "Доброй ночи";
}
